/**
 * Temporal state machine — Prompt.md §15, §18, §33.8.
 *
 * "Đây là phần quan trọng nhất của StudyStraight. Không cảnh báo dựa trên single frame."
 *
 * Máy trạng thái này chỉ đo **thời lượng liên tục** của từng issue rồi phát event. Nó không biết
 * đến giọng nói, cooldown hay câu chữ — đó là việc của AlertEngine (§18). Nhờ vậy toàn bộ luật
 * thời gian test được bằng cách bơm timestamp giả, không cần mock speech.
 */

import type { TemporalConfig } from "@studystraight/shared-config";
import type {
  EmittedSeverity,
  PostureEvent,
  PostureIssueType,
  PostureResult,
  PostureState,
} from "@studystraight/shared-types";

const ISSUES: readonly PostureIssueType[] = [
  "HEAD_DOWN",
  "LEANING_FORWARD",
  "TOO_CLOSE",
  "SHOULDER_TILT",
];

/** Thứ tự nặng dần — dùng để so sánh state và severity, không phải để hiển thị. */
const STATE_RANK: Record<PostureState, number> = {
  GOOD: 0,
  POTENTIAL_ISSUE: 1,
  NEEDS_ADJUSTMENT: 2,
  BAD: 3,
  ALERT: 4,
};

const SEVERITY_RANK: Record<EmittedSeverity, number> = { WARNING: 1, BAD: 2 };

/** Một chuỗi lỗi liên tục đang diễn ra. */
interface IssueRun {
  /** timestamp của frame đầu tiên trong chuỗi. */
  since: number;
  severity: EmittedSeverity;
  /** Lần gần nhất chuỗi này được phát thành event, để không phát mỗi frame. */
  lastEmittedAt: number | null;
}

export interface StateTransition {
  state: PostureState;
  events: readonly PostureEvent[];
}

export class PostureStateMachine {
  private state: PostureState = "GOOD";
  private readonly runs = new Map<PostureIssueType, IssueRun>();
  private goodSince: number | null = null;

  constructor(private readonly config: TemporalConfig) {}

  get current(): PostureState {
    return this.state;
  }

  push(result: PostureResult, timestamp: number): StateTransition {
    const { ignoreBelowMs, observeUntilMs, alertAfterMs, recoveryMs } = this.config;
    const events: PostureEvent[] = [];
    let worstDuration = 0;
    let worstSeverity = 0;

    for (const issue of ISSUES) {
      const severity = result.issues[issue];
      if (severity === "NONE") {
        this.runs.delete(issue);
        continue;
      }

      const run = this.runs.get(issue) ?? { since: timestamp, severity, lastEmittedAt: null };
      // Leo thang WARNING → BAD **không** reset đồng hồ: tư thế xấu dần đi vẫn là một chuỗi lỗi
      // liên tục. Reset ở đây sẽ khiến trẻ cúi ngày càng thấp mà không bao giờ đủ 5 giây để nhắc.
      run.severity = severity;
      this.runs.set(issue, run);

      const duration = timestamp - run.since;
      const dueAgain = run.lastEmittedAt === null || timestamp - run.lastEmittedAt >= alertAfterMs;
      if (duration >= alertAfterMs && dueAgain) {
        run.lastEmittedAt = timestamp;
        events.push({ type: issue, severity, duration, timestamp });
      }

      if (
        SEVERITY_RANK[severity] > worstSeverity ||
        (SEVERITY_RANK[severity] === worstSeverity && duration > worstDuration)
      ) {
        worstSeverity = SEVERITY_RANK[severity];
        worstDuration = duration;
      }
    }

    if (this.runs.size === 0) {
      this.goodSince ??= timestamp;
      // Hysteresis đường về (§15): chỉ tuyên bố GOOD sau `recoveryMs` giữ tư thế tốt liên tục.
      // Trong lúc chờ, hạ xuống NEEDS_ADJUSTMENT chứ không giữ nguyên BAD/ALERT — trẻ đã sửa tư
      // thế thì UI phải phản hồi ngay, còn việc "chưa chắc đã ổn định" thể hiện bằng việc chưa
      // về GOOD.
      this.state =
        timestamp - this.goodSince >= recoveryMs
          ? "GOOD"
          : STATE_RANK[this.state] > STATE_RANK.NEEDS_ADJUSTMENT
            ? "NEEDS_ADJUSTMENT"
            : this.state;
    } else {
      this.goodSince = null;
      this.state =
        events.length > 0
          ? "ALERT"
          : worstDuration >= observeUntilMs
            ? "BAD"
            : worstDuration >= ignoreBelowMs
              ? "NEEDS_ADJUSTMENT"
              : "POTENTIAL_ISSUE";
    }

    return { state: this.state, events };
  }

  /**
   * Mất tracking — xoá mọi đồng hồ nhưng **không** phát event.
   *
   * Trẻ rời khỏi khung hình không phải lỗi tư thế: nếu giữ nguyên đồng hồ, lúc trẻ quay lại sau
   * 2 phút hệ thống sẽ lập tức nhắc "con cúi đầu 120 giây rồi" — một câu vô nghĩa và sai.
   */
  interrupt(): void {
    this.runs.clear();
    this.goodSince = null;
  }

  reset(): void {
    this.interrupt();
    this.state = "GOOD";
  }
}
